/**
 * How an activity feed tells time (PROD-1054).
 *
 * The LMS dashboard had two feeds side by side in two styles: Live Activity
 * printed "5m ago" from English literals with the exact time on hover, and
 * Recent Activity printed date-fns "about 18 hours ago" with no exact time.
 * Both use these now, through <ActivityTime> (components/shared/activity-time).
 *
 * Relative text, short enough for a narrow column (Slack / GitHub pattern):
 *   < 1 min      → "just now"
 *   < 1 hour     → "5 min ago"
 *   today        → "3 h ago"
 *   yesterday    → "Yesterday 13:11"
 *   < 7 days     → "Sun 14:30"
 *   same year    → "Jun 11"
 *   older        → "Jun 11, 2025"
 *
 * Words come from the catalogue (activityTime.*), weekdays, months and the
 * exact instant from Intl in the interface language with Western digits
 * (intlLocaleFor). Days and times are the VIEWER's: docs/LOCALE.md §2.9 shows
 * instants on admin screens in the viewer's zone, so no zone is passed to Intl
 * and "yesterday" is the viewer's calendar yesterday.
 *
 * API values are read with parseApiDate: a zone-less timestamp is UTC.
 */
import { DATE_TIME, formatLearnerDate, intlLocaleFor } from '@/components/user-portal/learner-i18n'
import { parseApiDate } from './parse-api-date'

type Translate = (key: string, params?: Record<string, string | number>) => string
type ApiInstant = string | number | Date | null | undefined

const DAY_MS = 24 * 60 * 60 * 1000

/** The viewer's calendar day of `date`, as a day number that DST cannot skew. */
const localDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS

function format(date: Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat(intlLocaleFor(locale), options).format(date)
  } catch {
    return date.toISOString().slice(0, 16).replace('T', ' ')
  }
}

const TIME_OF_DAY: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }

/** "5 min ago", "Yesterday 13:11", "Jun 11" …; '' when there is no instant. */
export function formatActivityRelative(
  value: ApiInstant,
  t: Translate,
  locale: string,
  now: number | Date = Date.now(),
): string {
  const date = parseApiDate(value)
  if (!date) return ''
  const nowMs = typeof now === 'number' ? now : now.getTime()
  const diffSec = Math.round((nowMs - date.getTime()) / 1000)
  const diffMin = Math.round(diffSec / 60)
  const diffHr = Math.round(diffMin / 60)

  if (diffSec < 60) return t('activityTime.justNow')
  if (diffMin < 60) return t('activityTime.minutesAgo', { count: diffMin })

  const today = parseApiDate(nowMs) as Date
  const dayDiff = localDay(today) - localDay(date)
  if (dayDiff === 0) return t('activityTime.hoursAgo', { count: diffHr })
  if (dayDiff === 1) return t('activityTime.yesterday', { time: format(date, locale, TIME_OF_DAY) })
  if (dayDiff < 7) return format(date, locale, { weekday: 'short', ...TIME_OF_DAY })
  const sameYear = date.getFullYear() === today.getFullYear()
  return format(date, locale, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "Sep 30, 2026, 2:00:07 PM GMT+4" — the exact instant for the tooltip; '' when none. */
export function formatActivityExact(value: ApiInstant, locale: string): string {
  return formatLearnerDate(value, locale, { ...DATE_TIME, second: '2-digit', timeZoneName: 'short' })
}
